import { KnowledgeAskCitation, WebCitation } from '@digital-twins/shared-types'

import { citationSocialPost } from '../KnowledgeHubPage/social/SocialCitationPostMeta'
import { cleanWebUrl } from './furtherReadingItems'
import { citationDocumentKey } from './groupCitationsByDocument'
import { EvidenceCitationTarget, PartitionedEvidenceV2 } from './partitionAssistantCitations'
import { evidenceIdOf } from './RichBlocks/RichBlock'

/** The turn fields `aggregateThreadSources` reads. */
export interface ThreadSourcesTurn {
  role: 'user' | 'assistant'
  streaming?: boolean
  refused?: boolean
  /** Read for the `fairgen-kpis` / `fairgen-cards` fences a v2 answer cites from. */
  text?: string
  citations?: Array<KnowledgeAskCitation>
  webCitations?: Array<WebCitation>
  citationV2?: PartitionedEvidenceV2
}

/**
 * The occurrence a thread-wide row opens: the latest one, so a document
 * opens at its most recently cited passage. `index` is the 1-based number
 * that occurrence's chip carries in its own answer.
 */
export type ThreadSourceRepresentative =
  | { kind: 'target'; target: EvidenceCitationTarget; index: number }
  | { kind: 'legacy'; citation: KnowledgeAskCitation; index: number }
  | { kind: 'web'; citation: WebCitation; index: number }

export interface ThreadSourceEntry {
  /** Source identity across turns (`web:`, `doc:`, `social:`, `study:`, `room:`, `connector:`). */
  key: string
  /**
   * Citation points: one per answer span and one per rich-block item (KPI
   * tile, card) citing it; 1 for a citation the answer never placed.
   */
  count: number
  /** Position of the latest turn that cited it. */
  lastTurnIndex: number
  /** Order of the first occurrence in the thread, the last tie-break. */
  firstSeen: number
  representative: ThreadSourceRepresentative
}

interface Occurrence {
  key: string
  count: number
  representative: ThreadSourceRepresentative
}

const TRACKING_PARAM = /^(utm_.*|fbclid|gclid)$/i

/**
 * One web page however it was linked: protocol, `www.`, hash, tracking
 * params, param order and a trailing slash never split it. An unparseable
 * url keys on itself.
 */
export function normalizeWebUrl(url: string): string {
  const trimmed = url.trim()
  let parsed: URL
  try {
    parsed = new URL(cleanWebUrl(trimmed))
  } catch {
    return trimmed
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '')
  const port = parsed.port ? `:${parsed.port}` : ''
  const path = parsed.pathname.replace(/\/+$/, '')
  const params = [...parsed.searchParams.entries()]
    .filter(([name]) => !TRACKING_PARAM.test(name))
    .sort(([a, av], [b, bv]) => (a === b ? av.localeCompare(bv) : a.localeCompare(b)))
  const query = params.length ? `?${new URLSearchParams(params).toString()}` : ''
  return `${host}${port}${path}${query}`
}

function documentIdentity(citation: KnowledgeAskCitation, postToken?: string | null): string {
  const post = citationSocialPost(citation)
  const socialToken = post?.postToken || postToken || post?.url
  if (socialToken) {
    return `social:${socialToken}`
  }
  return `doc:${citationDocumentKey(citation)}`
}

/** Null for a target that is not a source a reader opens (calculations). */
function targetIdentity(target: EvidenceCitationTarget): string | null {
  switch (target.kind) {
    case 'web':
      return `web:${normalizeWebUrl(target.citation.url)}`
    case 'calculation':
      return null
    case 'study':
      return `study:${target.evidence.studyToken}`
    case 'room':
      return `room:${target.evidence.roomToken}`
    case 'connector_record':
      return `connector:${target.evidence.app}:${target.evidence.recordKey}`
    case 'page_region':
      return `doc:${citationDocumentKey(target.citation)}`
    case 'document': {
      const postRef = target.evidence.kind === 'social' ? target.evidence.postRef : null
      return documentIdentity(target.citation, postRef?.postToken ?? postRef?.url)
    }
  }
}

const RICH_FENCE = /```(?:fairgen-kpis|fairgen-cards)[^\n]*\n([\s\S]*?)```/g

function targetEvidenceId(target: EvidenceCitationTarget | null): string | null {
  if (!target) {
    return null
  }
  return target.kind === 'web' ? target.citation.id : target.evidence.id
}

/**
 * The evidence ids each rich-block item cites, once per item. Items are the
 * fence's JSON array (KPI tiles, cards); `cite` is one ref or a list of refs.
 * A fence that does not parse cites nothing, as it renders nothing.
 */
function richBlockCites(text: string): Array<Set<string>> {
  const items: Array<Set<string>> = []
  for (const match of text.matchAll(RICH_FENCE)) {
    let parsed: unknown
    try {
      parsed = JSON.parse(match[1])
    } catch {
      continue
    }
    if (!Array.isArray(parsed)) {
      continue
    }
    for (const item of parsed) {
      const cite = (item as { cite?: unknown } | null)?.cite
      const refs = Array.isArray(cite) ? cite : [cite]
      const ids = new Set<string>()
      for (const ref of refs) {
        const id = typeof ref === 'string' ? evidenceIdOf(ref) : null
        if (id) {
          ids.add(id)
        }
      }
      if (ids.size > 0) {
        items.push(ids)
      }
    }
  }
  return items
}

function v2Occurrences(v2: PartitionedEvidenceV2, text: string): Array<Occurrence> {
  const spansPerTarget = new Map<number, number>()
  for (const span of v2.answerSpans) {
    if (!span || !Array.isArray(span.citationIndexes)) {
      continue
    }
    for (const index of new Set(span.citationIndexes)) {
      spansPerTarget.set(index, (spansPerTarget.get(index) ?? 0) + 1)
    }
  }
  const positionById = new Map<string, number>()
  v2.targets.forEach((target, position) => {
    const id = targetEvidenceId(target)
    if (id && !positionById.has(id)) {
      positionById.set(id, position)
    }
  })
  for (const ids of richBlockCites(text)) {
    for (const id of ids) {
      const position = positionById.get(id)
      if (position !== undefined) {
        spansPerTarget.set(position, (spansPerTarget.get(position) ?? 0) + 1)
      }
    }
  }
  // Several targets can share one identity (the same page or post twice):
  // their placed points add up, and the source still earns one point when
  // none of them was placed, never one per unplaced target.
  const byKey = new Map<string, Occurrence>()
  v2.targets.forEach((target, position) => {
    if (!target) {
      return
    }
    const key = targetIdentity(target)
    if (!key) {
      return
    }
    const placed = spansPerTarget.get(position) ?? 0
    const existing = byKey.get(key)
    if (existing) {
      existing.count += placed
    } else {
      byKey.set(key, { key, count: placed, representative: { kind: 'target', target, index: position + 1 } })
    }
  })
  const occurrences = [...byKey.values()]
  for (const occurrence of occurrences) {
    occurrence.count = Math.max(1, occurrence.count)
  }
  return occurrences
}

function legacyOccurrences(turn: ThreadSourcesTurn): Array<Occurrence> {
  const occurrences: Array<Occurrence> = []
  ;(turn.citations ?? []).forEach((citation, position) => {
    occurrences.push({
      key: documentIdentity(citation),
      count: 1,
      representative: { kind: 'legacy', citation, index: position + 1 },
    })
  })
  // Legacy webCitations hold one entry per snippet, and the answer shows one
  // chip per page, so a page counts once per answer.
  const seenWebKeys = new Set<string>()
  ;(turn.webCitations ?? []).forEach((citation, position) => {
    if (!citation.url) {
      return
    }
    const key = `web:${normalizeWebUrl(citation.url)}`
    if (seenWebKeys.has(key)) {
      return
    }
    seenWebKeys.add(key)
    occurrences.push({
      key,
      count: 1,
      representative: { kind: 'web', citation, index: position },
    })
  })
  return occurrences
}

const occurrenceCache = new WeakMap<ThreadSourcesTurn, Array<Occurrence>>()

/** A settled turn's occurrences, scanned once per turn object. */
function turnOccurrences(turn: ThreadSourcesTurn): Array<Occurrence> {
  const cached = occurrenceCache.get(turn)
  if (cached) {
    return cached
  }
  // A v2 turn's targets already include its web and document citations.
  const occurrences = turn.citationV2 ? v2Occurrences(turn.citationV2, turn.text ?? '') : legacyOccurrences(turn)
  occurrenceCache.set(turn, occurrences)
  return occurrences
}

/**
 * Every source the thread's answers cited, once each, the most cited first
 * (then the most recently used). Settled assistant answers only: a streaming
 * or refused answer adds nothing. Calculations are not sources here.
 */
export function aggregateThreadSources(turns: ReadonlyArray<ThreadSourcesTurn>): Array<ThreadSourceEntry> {
  const entries = new Map<string, ThreadSourceEntry>()
  turns.forEach((turn, turnIndex) => {
    if (turn.role !== 'assistant' || turn.streaming || turn.refused) {
      return
    }
    for (const occurrence of turnOccurrences(turn)) {
      const existing = entries.get(occurrence.key)
      if (existing) {
        existing.count += occurrence.count
        existing.lastTurnIndex = turnIndex
        existing.representative = occurrence.representative
      } else {
        entries.set(occurrence.key, {
          key: occurrence.key,
          count: occurrence.count,
          lastTurnIndex: turnIndex,
          firstSeen: entries.size,
          representative: occurrence.representative,
        })
      }
    }
  })
  return [...entries.values()].sort(
    (a, b) => b.count - a.count || b.lastTurnIndex - a.lastTurnIndex || a.firstSeen - b.firstSeen,
  )
}
